import { computed, inject, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue';
import { getVisibleTagItems } from '@/common/utils/tag.util';
import { AlertTargetItem } from '@/alert/components/alertTargetList/alertTargetList.types';
import { useRoute } from 'vue-router';
import { CATEGORY_TARGET_TRIGGER_TYPE_MAP } from '@/ai/components/targetDetail.define';
import { EVENT_NAME_ENUM } from '@/alert/utils/define';
import { useAlertTargetDetail } from './alertTargetDetail.use';

export interface Props {
  targetList: AlertTargetItem[];
  shownCount?: number;
  isExpand?: boolean;
  isClickable?: boolean;
  isAliasUsed?: boolean;
  rowData?: Record<string, any>; // TODO: 타입 정의
  timePeriodInfo?: {
    fromTime: string;
    toTime: string;
  };
}

const INCLUDE_CATEGORY = ['rum'];

/** alias → name → id 순서로 표시할 레이블을 반환 */
const resolveTargetLabel = (item: AlertTargetItem, isAliasUsed: boolean): string =>
  (isAliasUsed ? item.alias || item.name || item.id : item.name || item.id) ?? '';

const isClickableTag = (isClickable?: boolean, category?: string): boolean => {
  if (!isClickable || !category) {
    return false;
  }
  return !!(CATEGORY_TARGET_TRIGGER_TYPE_MAP?.[category] || INCLUDE_CATEGORY.includes(category));
};

const isClickableRowData = (rowData?: Record<string, any>): boolean => {
  if (!rowData) {
    return true;
  }

  return (
    rowData.eventType !== EVENT_NAME_ENUM.PROCESS_DOWN_DETECTION &&
    rowData.eventType !== EVENT_NAME_ENUM.PROCESS_COUNT_DETECTION
  );
};

const useTargetDetail = (props: Props) => {
  const emitOpenDetail = inject<(...args: any[]) => void>('emitOpenDetail', () => {});
  const { setTypeDetail } = useAlertTargetDetail();

  const openDetail = async (item: AlertTargetItem) => {
    if (!isClickableTag(props.isClickable, item.category)) {
      return;
    }

    if (!isClickableRowData(props.rowData)) {
      return;
    }

    const sourceName = item.originalName || item.name || '';
    const prefix = `${item.category}:`;
    const name = sourceName.startsWith(prefix) ? sourceName.slice(prefix.length) : sourceName;
    let { category = '' } = item;

    if (item.category === 'process') {
      category = 'processDetailInfo';
    }

    await setTypeDetail({
      name,
      category,
      targetId: item.id!,
      timePeriodInfo: props.timePeriodInfo,
    });

    if (typeof emitOpenDetail === 'function') {
      emitOpenDetail();
    }
  };

  return {
    openDetail,
  };
};

const setup = (props: Props) => {
  const { name } = useRoute();
  const isBrowserPopup = computed(() => name === 'BrowserPopup');
  const { openDetail } = useTargetDetail(props);

  const tagWrapperRef = ref<HTMLElement>();
  const targetsTooltipRef = ref();

  const computedTargetList = computed(() => {
    if (!props.targetList?.length) return [];

    return props.targetList.map((item) => ({
      ...item,
      clickable: isClickableTag(props.isClickable, item.category),
      value: resolveTargetLabel(item, props.isAliasUsed ?? false),
      name: resolveTargetLabel(item, props.isAliasUsed ?? false),
      originalName: item.name,
    }));
  });

  const visibleTagItems = shallowRef<AlertTargetItem[]>([]);
  const moreCount = shallowRef<number>(0);

  let resizeObserver: ResizeObserver | null = null;

  const getMoreCount = () => {
    if (!computedTargetList.value?.length || props.isExpand) {
      return 0;
    }
    return computedTargetList.value.length - visibleTagItems.value.length;
  };

  const setVisibleTagItems = () => {
    if (!tagWrapperRef.value || !computedTargetList.value?.length) {
      return;
    }

    if (props.isExpand) {
      visibleTagItems.value = computedTargetList.value;
      moreCount.value = 0;
      return;
    }

    if (typeof props.shownCount === 'number') {
      visibleTagItems.value = computedTargetList.value.slice(0, props.shownCount);
      moreCount.value = computedTargetList.value.length - props.shownCount;
      targetsTooltipRef.value?.hide();
      return;
    }

    visibleTagItems.value = getVisibleTagItems<string>({
      wrapperEl: tagWrapperRef.value,
      itemList: computedTargetList.value,
      options: {
        wrapperPadding: 0,
        tagMaxWidthPercent: 1,
      },
    });

    moreCount.value = getMoreCount();
    targetsTooltipRef.value?.hide();
  };

  const isClickableTarget = (item: AlertTargetItem) => {
    return item.clickable && isClickableRowData(props.rowData);
  };

  watch(
    () => [props.targetList, props.isExpand, props.shownCount, props.isClickable],
    () => {
      setVisibleTagItems();
    },
    { immediate: true },
  );

  onMounted(() => {
    if (!props.isExpand && tagWrapperRef.value) {
      resizeObserver = new ResizeObserver(() => {
        setVisibleTagItems();
      });
      resizeObserver.observe(tagWrapperRef.value);
    } else {
      setVisibleTagItems();
    }
  });

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  const tooltipList = shallowRef<AlertTargetItem[]>([]);

  const onClickMore = (e: MouseEvent) => {
    if (moreCount.value > 0) {
      tooltipList.value = [...props.targetList];
      targetsTooltipRef.value?.show(e);
    }
  };

  const onClickTag = (item: AlertTargetItem, e?: MouseEvent) => {
    if (!item.clickable || isBrowserPopup.value) {
      return;
    }

    e?.stopPropagation();

    targetsTooltipRef.value?.hide();
    tooltipList.value = [];
    openDetail(item);
  };

  return {
    isBrowserPopup,
    tagWrapperRef,
    visibleTagItems,

    tooltipList,
    moreCount,

    targetsTooltipRef,

    isClickableTarget,
    onClickMore,
    onClickTag,
  };
};

export { setup, resolveTargetLabel };
